import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  obtenerObjetoAdmin,
  actualizarObjetoAdmin,
  toggleOcultoAdmin,
  toggleDemoAdmin,
  resetearCodigoAdmin,
  eliminarObjetoAdmin,
  resetDatosAdmin
} from '../../../services/api/modules/admin';

/**
 * Modal completo para editar un registro desde el Admin Panel.
 * Incluye:
 * - Sección 1: Datos Básicos (editables)
 * - Sección 2: Propietario (editables)
 * - Sección 3: Fechas (editables según plan)
 * - Sección 4: Autenticidad (SOLO LECTURA)
 * - Sección 5: Veterinarios/Proveedores (solo para coleccionables)
 * - Sección 6: Acciones Especiales
 */
function EditarRegistroModal({ show, onClose, objetoId, onSuccess }) {
  // Estados del componente
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('basicos');

  // Datos originales (para comparar cambios)
  const [originalData, setOriginalData] = useState(null);

  // Datos editables
  const [formData, setFormData] = useState({
    // Datos básicos
    nombre: '',
    tipo: '',
    plan: '',
    estado: '',
    descripcion: '',
    // Mascota específicos
    raza: '',
    especie: '',
    chip: '',
    vacunado: false,
    esterilizado: false,
    // Persona específicos
    tipoSangrePropietario: '',
    alergias: '',
    condicionesMedicas: '',
    personaDeContacto: '',
    telefonoDelContacto: '',
    // Llavero específicos
    coleccion: '',
    numeroPieza: '',
    totalPiezas: '',
    // Propietario
    nombrePropietario: '',
    correoPropietario: '',
    telefonoPropietario: '',
    direccion: '',
    // Fechas
    fechaVencimiento: '',
    duracionPlanMeses: 12,
    // Veterinario/Proveedor IDs
    veterinarioId: '',
    proveedorId: ''
  });

  // Estado para acciones especiales
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [newActivationCode, setNewActivationCode] = useState(null);

  // Estados para resetear datos
  const [resetFlags, setResetFlags] = useState({
    vacunas: false,
    visitas: false,
    desparasitaciones: false,
    citas: false,
    recordatorios: false,
    lugaresConfianza: false,
    veterinarios: false,
    documentos: false,
  });
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetting, setResetting] = useState(false);

  // Cargar datos del objeto cuando se abre el modal
  useEffect(() => {
    if (show && objetoId) {
      loadObjectData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, objetoId]);

  const loadObjectData = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await obtenerObjetoAdmin(objetoId);
      if (result.error) {
        setError(result.error);
        return;
      }
      const obj = result.data || result;

      setOriginalData(obj);

      // Manejar múltiples formatos posibles de datos del propietario
      // El objeto puede tener los datos directamente o bajo un objeto "propietario"
      const propietarioData = obj.propietario || {};

      setFormData({
        // Datos básicos
        nombre: obj.nombre || '',
        tipo: obj.tipo || '',
        plan: obj.plan || 'light',
        estado: obj.estado || '',
        descripcion: obj.descripcion || '',
        extraviado: obj.extraviado || false,
        bloqueado: obj.bloqueado || false,
        oculto: obj.oculto || false,
        esDemo: obj.esDemo || false,
        // Mascota
        raza: obj.raza || '',
        especie: obj.especie || '',
        chip: obj.chip || '',
        vacunado: obj.vacunado || false,
        esterilizado: obj.esterilizado || false,
        // Persona
        tipoSangrePropietario: obj.tipoSangrePropietario || '',
        alergias: obj.alergias || '',
        condicionesMedicas: obj.condicionesMedicas || '',
        personaDeContacto: obj.personaDeContacto || '',
        telefonoDelContacto: obj.telefonoDelContacto || '',
        // Llavero
        coleccion: obj.coleccion || '',
        numeroPieza: obj.numeroPieza || '',
        totalPiezas: obj.totalPiezas || '',
        // Propietario - buscar en múltiples ubicaciones posibles
        nombrePropietario: obj.nombrePropietario || propietarioData.nombre || obj.nombre_propietario || '',
        correoPropietario: obj.correoPropietario || propietarioData.correo || propietarioData.email || obj.correo_propietario || obj.email || '',
        telefonoPropietario: obj.telefonoPropietario || propietarioData.telefono || propietarioData.whatsapp || obj.telefono_propietario || obj.telefono || '',
        direccion: obj.direccion || propietarioData.direccion || '',
        // Fechas
        fechaVencimiento: obj.fechaVencimiento || '',
        duracionPlanMeses: obj.duracionPlanMeses || 12,
        // Veterinario/Proveedor
        veterinarioId: obj.veterinarioId || '',
        proveedorId: obj.proveedorId || ''
      });
    } catch (err) {
      setError(err.message || 'Error al cargar el registro');
    } finally {
      setLoading(false);
    }
  };

  // Handler genérico para cambios en el formulario
  const handleChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  // Guardar cambios
  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      // Construir objeto de cambios (solo campos modificados)
      const cambios = {};
      Object.keys(formData).forEach(key => {
        if (formData[key] !== originalData[key]) {
          cambios[key] = formData[key];
        }
      });

      if (Object.keys(cambios).length === 0) {
        toast.info('No hay cambios para guardar');
        setSaving(false);
        return;
      }

      const result = await actualizarObjetoAdmin(objetoId, cambios);

      if (result.error) {
        setError(result.error);
        toast.error(result.error);
        return;
      }

      toast.success('Registro actualizado correctamente');
      if (onSuccess) {
        onSuccess(result.data || result);
      }
      onClose();
    } catch (err) {
      setError(err.message || 'Error al guardar');
      toast.error(err.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  // Toggle oculto
  const handleToggleOculto = async () => {
    try {
      const result = await toggleOcultoAdmin(objetoId);
      if (result.error) {
        toast.error(result.error);
        return;
      }
      const nuevoEstado = result.oculto;
      setFormData(prev => ({ ...prev, oculto: nuevoEstado }));
      setOriginalData(prev => ({ ...prev, oculto: nuevoEstado }));
      toast.success(nuevoEstado ? 'Registro ocultado del usuario' : 'Registro visible para el usuario');
    } catch (err) {
      toast.error(err.message || 'Error al cambiar visibilidad');
    }
  };

  // Toggle modo demo
  const handleToggleDemo = async () => {
    try {
      const result = await toggleDemoAdmin(objetoId);
      if (result.error) {
        toast.error(result.error);
        return;
      }
      const nuevoEstado = result.esDemo;
      setFormData(prev => ({ ...prev, esDemo: nuevoEstado }));
      setOriginalData(prev => ({ ...prev, esDemo: nuevoEstado }));
      toast.success(nuevoEstado ? 'Modo demo activado' : 'Modo demo desactivado');
    } catch (err) {
      toast.error(err.message || 'Error al cambiar modo demo');
    }
  };

  // Resetear código de activación
  const handleResetCodigo = async () => {
    if (!window.confirm('ADVERTENCIA: Esto generará un nuevo código de activación. El código anterior dejará de funcionar. ¿Continuar?')) {
      return;
    }
    try {
      const result = await resetearCodigoAdmin(objetoId);
      if (result.error) {
        toast.error(result.error);
        return;
      }
      setNewActivationCode(result.codigoNuevo);
      toast.success('Código de activación regenerado');
    } catch (err) {
      toast.error(err.message || 'Error al resetear código');
    }
  };

  // Resetear datos
  const resetLabels = {
    vacunas: 'Vacunas y cartilla de salud',
    visitas: 'Visitas/Historial veterinario (con documentos)',
    desparasitaciones: 'Desparasitaciones',
    citas: 'Citas programadas',
    recordatorios: 'Recordatorios',
    lugaresConfianza: 'Lugares de confianza',
    veterinarios: 'Veterinarios asociados',
    documentos: 'Documentos de la bóveda',
  };

  const selectedResetCount = Object.values(resetFlags).filter(Boolean).length;

  const handleResetDatos = async () => {
    if (selectedResetCount === 0) {
      toast.error('Selecciona al menos una categoría');
      return;
    }
    setResetting(true);
    try {
      const result = await resetDatosAdmin(objetoId, resetFlags);
      if (result.error) {
        toast.error(result.error);
        return;
      }
      const detalle = result.detalle || {};
      const resumen = Object.entries(detalle)
        .filter(([, v]) => v > 0)
        .map(([k, v]) => `${v} ${resetLabels[k] || k}`)
        .join(', ');
      toast.success(resumen ? `Eliminados: ${resumen}` : 'No se encontraron registros para eliminar');
      setShowResetConfirm(false);
      setResetFlags({
        vacunas: false, visitas: false, desparasitaciones: false, citas: false,
        recordatorios: false, lugaresConfianza: false, veterinarios: false, documentos: false,
      });
    } catch (err) {
      toast.error(err.message || 'Error al resetear datos');
    } finally {
      setResetting(false);
    }
  };

  // Eliminar registro
  const handleDelete = async () => {
    if (deleteConfirmText !== 'ELIMINAR_PERMANENTEMENTE') {
      toast.error('Debes escribir ELIMINAR_PERMANENTEMENTE para confirmar');
      return;
    }
    try {
      const result = await eliminarObjetoAdmin(objetoId, deleteConfirmText);
      if (result.error) {
        toast.error(result.error);
        return;
      }
      toast.success(`Registro eliminado: Folio ${result.folioEliminado}`);
      if (onSuccess) {
        onSuccess({ deleted: true, id: objetoId });
      }
      onClose();
    } catch (err) {
      toast.error(err.message || 'Error al eliminar');
    }
  };

  if (!show) return null;

  // Renderizar campos según el tipo de objeto
  const renderTypeSpecificFields = () => {
    switch (formData.tipo) {
      case 'mascota':
        return (
          <>
            <div className="row g-2">
              <div className="col-md-6">
                <label className="form-label">Raza</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.raza}
                  onChange={(e) => handleChange('raza', e.target.value)}
                  disabled={saving}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Especie</label>
                <select
                  className="form-select"
                  value={formData.especie}
                  onChange={(e) => handleChange('especie', e.target.value)}
                  disabled={saving}
                >
                  <option value="">Seleccionar...</option>
                  <option value="perro">Perro</option>
                  <option value="gato">Gato</option>
                  <option value="ave">Ave</option>
                  <option value="otro">Otro</option>
                </select>
              </div>
            </div>
            <div className="row g-2 mt-2">
              <div className="col-md-6">
                <label className="form-label">N° de Chip</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.chip}
                  onChange={(e) => handleChange('chip', e.target.value)}
                  disabled={saving}
                />
              </div>
              <div className="col-md-6 d-flex align-items-end gap-3">
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="vacunado"
                    checked={formData.vacunado}
                    onChange={(e) => handleChange('vacunado', e.target.checked)}
                    disabled={saving}
                  />
                  <label className="form-check-label" htmlFor="vacunado">Vacunado</label>
                </div>
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="esterilizado"
                    checked={formData.esterilizado}
                    onChange={(e) => handleChange('esterilizado', e.target.checked)}
                    disabled={saving}
                  />
                  <label className="form-check-label" htmlFor="esterilizado">Esterilizado</label>
                </div>
              </div>
            </div>
          </>
        );

      case 'persona':
        return (
          <>
            <div className="row g-2">
              <div className="col-md-4">
                <label className="form-label">Tipo de Sangre</label>
                <select
                  className="form-select"
                  value={formData.tipoSangrePropietario}
                  onChange={(e) => handleChange('tipoSangrePropietario', e.target.value)}
                  disabled={saving}
                >
                  <option value="">Seleccionar...</option>
                  <option value="A+">A+</option>
                  <option value="A-">A-</option>
                  <option value="B+">B+</option>
                  <option value="B-">B-</option>
                  <option value="AB+">AB+</option>
                  <option value="AB-">AB-</option>
                  <option value="O+">O+</option>
                  <option value="O-">O-</option>
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label">Persona de Contacto</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.personaDeContacto}
                  onChange={(e) => handleChange('personaDeContacto', e.target.value)}
                  disabled={saving}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Teléfono del Contacto</label>
                <input
                  type="tel"
                  className="form-control"
                  value={formData.telefonoDelContacto}
                  onChange={(e) => handleChange('telefonoDelContacto', e.target.value)}
                  disabled={saving}
                />
              </div>
            </div>
            <div className="row g-2 mt-2">
              <div className="col-md-6">
                <label className="form-label">Alergias</label>
                <textarea
                  className="form-control"
                  rows="2"
                  value={formData.alergias}
                  onChange={(e) => handleChange('alergias', e.target.value)}
                  disabled={saving}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Condiciones Médicas</label>
                <textarea
                  className="form-control"
                  rows="2"
                  value={formData.condicionesMedicas}
                  onChange={(e) => handleChange('condicionesMedicas', e.target.value)}
                  disabled={saving}
                />
              </div>
            </div>
          </>
        );

      case 'llavero':
        return (
          <div className="row g-2">
            <div className="col-md-4">
              <label className="form-label">Colección</label>
              <input
                type="text"
                className="form-control"
                value={formData.coleccion}
                onChange={(e) => handleChange('coleccion', e.target.value)}
                disabled={saving}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">N° de Pieza</label>
              <input
                type="number"
                className="form-control"
                value={formData.numeroPieza}
                onChange={(e) => handleChange('numeroPieza', e.target.value)}
                disabled={saving}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Total de Piezas</label>
              <input
                type="number"
                className="form-control"
                value={formData.totalPiezas}
                onChange={(e) => handleChange('totalPiezas', e.target.value)}
                disabled={saving}
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div
      className="modal fade show d-block"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
      onClick={onClose}
    >
      <div
        className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content">
          {/* Header */}
          <div className="modal-header bg-primary text-white">
            <h5 className="modal-title">
              <i className="bi bi-pencil-square me-2"></i>
              Editar Registro
              {originalData && (
                <small className="ms-2 badge bg-light text-dark">
                  Folio: {originalData.folio}
                </small>
              )}
            </h5>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={onClose}
              disabled={saving}
            ></button>
          </div>

          {/* Body */}
          <div className="modal-body">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Cargando...</span>
                </div>
                <p className="mt-3 text-muted">Cargando datos del registro...</p>
              </div>
            ) : error && !originalData ? (
              <div className="alert alert-danger">
                <i className="bi bi-exclamation-triangle me-2"></i>
                {error}
              </div>
            ) : (
              <>
                {/* Badges de estado */}
                <div className="d-flex flex-wrap gap-2 mb-3">
                  {formData.oculto && (
                    <span className="badge bg-secondary">
                      <i className="bi bi-eye-slash me-1"></i>Oculto
                    </span>
                  )}
                  {formData.extraviado && (
                    <span className="badge bg-danger">
                      <i className="bi bi-exclamation-triangle me-1"></i>PERDIDO
                    </span>
                  )}
                  {formData.bloqueado && (
                    <span className="badge bg-warning text-dark">
                      <i className="bi bi-lock me-1"></i>Bloqueado
                    </span>
                  )}
                  {formData.esDemo && (
                    <span className="badge bg-info">
                      <i className="bi bi-flask me-1"></i>Demo
                    </span>
                  )}
                  <span className={`badge ${formData.estado === 'activo' ? 'bg-success' : 'bg-info'}`}>
                    {formData.estado || 'Sin estado'}
                  </span>
                </div>

                {/* Tabs de navegación */}
                <ul className="nav nav-tabs mb-3">
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activeTab === 'basicos' ? 'active' : ''}`}
                      onClick={() => setActiveTab('basicos')}
                    >
                      <i className="bi bi-card-list me-1"></i>
                      Datos Básicos
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activeTab === 'propietario' ? 'active' : ''}`}
                      onClick={() => setActiveTab('propietario')}
                    >
                      <i className="bi bi-person me-1"></i>
                      Propietario
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activeTab === 'fechas' ? 'active' : ''}`}
                      onClick={() => setActiveTab('fechas')}
                    >
                      <i className="bi bi-calendar me-1"></i>
                      Fechas
                    </button>
                  </li>
                  {formData.tipo === 'coleccionable' && (
                    <li className="nav-item">
                      <button
                        className={`nav-link ${activeTab === 'autenticidad' ? 'active' : ''}`}
                        onClick={() => setActiveTab('autenticidad')}
                      >
                        <i className="bi bi-shield-check me-1"></i>
                        Autenticidad
                      </button>
                    </li>
                  )}
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activeTab === 'acciones' ? 'active' : ''} text-danger`}
                      onClick={() => setActiveTab('acciones')}
                    >
                      <i className="bi bi-gear me-1"></i>
                      Acciones
                    </button>
                  </li>
                </ul>

                {/* Contenido de cada tab */}
                <div className="tab-content">
                  {/* Tab: Datos Básicos */}
                  {activeTab === 'basicos' && (
                    <div className="tab-pane fade show active">
                      {/* ID del objeto (solo lectura) */}
                      <div className="mb-3 p-2 bg-light rounded border">
                        <div className="d-flex align-items-center justify-content-between">
                          <div>
                            <small className="text-muted d-block">ID del registro</small>
                            <code className="text-dark" style={{ fontSize: '0.9rem' }}>
                              {objetoId}
                            </code>
                          </div>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => {
                              navigator.clipboard.writeText(objetoId);
                              toast.success('ID copiado al portapapeles');
                            }}
                            title="Copiar ID"
                          >
                            <i className="bi bi-clipboard me-1"></i>
                            Copiar
                          </button>
                        </div>
                      </div>

                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Nombre</label>
                          <input
                            type="text"
                            className="form-control"
                            value={formData.nombre}
                            onChange={(e) => handleChange('nombre', e.target.value)}
                            disabled={saving}
                          />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label fw-bold">Tipo</label>
                          <input
                            type="text"
                            className="form-control"
                            value={formData.tipo}
                            disabled
                          />
                          <small className="text-muted">No editable</small>
                        </div>
                        <div className="col-md-3">
                          <label className="form-label fw-bold">Plan</label>
                          <select
                            className="form-select"
                            value={formData.plan}
                            onChange={(e) => handleChange('plan', e.target.value)}
                            disabled={saving}
                          >
                            <option value="light">Light</option>
                            <option value="standard">Standard</option>
                            <option value="premium">Premium</option>
                          </select>
                        </div>
                      </div>

                      <div className="row g-3 mt-2">
                        <div className="col-md-4">
                          <label className="form-label fw-bold">Estado</label>
                          <select
                            className="form-select"
                            value={formData.estado}
                            onChange={(e) => handleChange('estado', e.target.value)}
                            disabled={saving}
                          >
                            <option value="inactivo">Inactivo</option>
                            <option value="pendientePorLigar">Pendiente por ligar</option>
                            <option value="pendienteVerificacion">Pendiente verificación</option>
                            <option value="activo">Activo</option>
                          </select>
                        </div>
                        <div className="col-md-4 d-flex align-items-end">
                          <div className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="extraviado"
                              checked={formData.extraviado}
                              onChange={(e) => handleChange('extraviado', e.target.checked)}
                              disabled={saving}
                            />
                            <label className="form-check-label text-danger" htmlFor="extraviado">
                              <i className="bi bi-exclamation-triangle me-1"></i>
                              Marcar como PERDIDO
                            </label>
                          </div>
                        </div>
                        <div className="col-md-4 d-flex align-items-end">
                          <div className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="bloqueado"
                              checked={formData.bloqueado}
                              onChange={(e) => handleChange('bloqueado', e.target.checked)}
                              disabled={saving}
                            />
                            <label className="form-check-label text-warning" htmlFor="bloqueado">
                              <i className="bi bi-lock me-1"></i>
                              Bloqueado
                            </label>
                          </div>
                        </div>
                      </div>

                      <div className="mt-3">
                        <label className="form-label fw-bold">Descripción</label>
                        <textarea
                          className="form-control"
                          rows="2"
                          value={formData.descripcion}
                          onChange={(e) => handleChange('descripcion', e.target.value)}
                          disabled={saving}
                        />
                      </div>

                      {/* Campos específicos según tipo */}
                      {formData.tipo && (
                        <div className="mt-4 pt-3 border-top">
                          <h6 className="text-muted mb-3">
                            Campos específicos de {formData.tipo}
                          </h6>
                          {renderTypeSpecificFields()}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Tab: Propietario */}
                  {activeTab === 'propietario' && (
                    <div className="tab-pane fade show active">
                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Nombre del Propietario</label>
                          <input
                            type="text"
                            className="form-control"
                            value={formData.nombrePropietario}
                            onChange={(e) => handleChange('nombrePropietario', e.target.value)}
                            disabled={saving}
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Correo</label>
                          <input
                            type="email"
                            className="form-control"
                            value={formData.correoPropietario}
                            onChange={(e) => handleChange('correoPropietario', e.target.value)}
                            disabled={saving}
                          />
                        </div>
                      </div>
                      <div className="row g-3 mt-2">
                        <div className="col-md-4">
                          <label className="form-label fw-bold">Teléfono</label>
                          <input
                            type="tel"
                            className="form-control"
                            value={formData.telefonoPropietario}
                            onChange={(e) => handleChange('telefonoPropietario', e.target.value)}
                            disabled={saving}
                          />
                        </div>
                        <div className="col-md-8">
                          <label className="form-label fw-bold">Dirección</label>
                          <input
                            type="text"
                            className="form-control"
                            value={formData.direccion}
                            onChange={(e) => handleChange('direccion', e.target.value)}
                            disabled={saving}
                          />
                        </div>
                      </div>

                      {/* Info del usuario ligado */}
                      {originalData?.ownerId && (
                        <div className="alert alert-info mt-3">
                          <i className="bi bi-info-circle me-2"></i>
                          <strong>Usuario ligado:</strong> {originalData.ownerId}
                          <br />
                          <small className="text-muted">
                            Este registro está vinculado a una cuenta de usuario
                          </small>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Tab: Fechas */}
                  {activeTab === 'fechas' && (
                    <div className="tab-pane fade show active">
                      {formData.plan === 'light' ? (
                        <div className="alert alert-secondary">
                          <i className="bi bi-infinity me-2"></i>
                          <strong>Plan Light:</strong> Los planes Light son permanentes y no tienen fecha de vencimiento.
                        </div>
                      ) : (
                        <>
                          <div className="row g-3">
                            <div className="col-md-6">
                              <label className="form-label fw-bold">Fecha de Vencimiento</label>
                              <input
                                type="date"
                                className="form-control"
                                value={formData.fechaVencimiento ? formData.fechaVencimiento.split('T')[0] : ''}
                                onChange={(e) => handleChange('fechaVencimiento', e.target.value)}
                                disabled={saving}
                              />
                            </div>
                            <div className="col-md-6">
                              <label className="form-label fw-bold">Duración del Plan (meses)</label>
                              <input
                                type="number"
                                className="form-control"
                                min="1"
                                max="60"
                                value={formData.duracionPlanMeses}
                                onChange={(e) => handleChange('duracionPlanMeses', parseInt(e.target.value) || 12)}
                                disabled={saving}
                              />
                            </div>
                          </div>

                          {/* Fechas de solo lectura */}
                          <div className="mt-4 pt-3 border-top">
                            <h6 className="text-muted mb-3">
                              <i className="bi bi-lock me-1"></i>
                              Fechas del sistema (solo lectura)
                            </h6>
                            <div className="row g-2 text-muted">
                              <div className="col-md-4">
                                <small><strong>Creación:</strong></small><br />
                                <small>{originalData?.createdAt || 'N/A'}</small>
                              </div>
                              <div className="col-md-4">
                                <small><strong>Activación:</strong></small><br />
                                <small>{originalData?.fechaActivacion || 'N/A'}</small>
                              </div>
                              <div className="col-md-4">
                                <small><strong>Última modificación:</strong></small><br />
                                <small>{originalData?.updatedAt || 'N/A'}</small>
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* Tab: Autenticidad (solo coleccionables) */}
                  {activeTab === 'autenticidad' && formData.tipo === 'coleccionable' && (
                    <div className="tab-pane fade show active">
                      <div className="alert alert-warning mb-3">
                        <i className="bi bi-shield-lock me-2"></i>
                        <strong>Campos de autenticidad:</strong> Estos campos son inmutables para garantizar la autenticidad del coleccionable.
                      </div>

                      <div className="card bg-light">
                        <div className="card-body">
                          <div className="row g-3">
                            <div className="col-md-6">
                              <label className="form-label text-muted">Número de Serie</label>
                              <input
                                type="text"
                                className="form-control bg-white"
                                value={originalData?.numeroSerie || 'N/A'}
                                disabled
                              />
                            </div>
                            <div className="col-md-6">
                              <label className="form-label text-muted">Creado Para</label>
                              <input
                                type="text"
                                className="form-control bg-white"
                                value={originalData?.creadoPara || 'N/A'}
                                disabled
                              />
                            </div>
                          </div>
                          <div className="row g-3 mt-2">
                            <div className="col-md-4">
                              <label className="form-label text-muted">Folio</label>
                              <input
                                type="text"
                                className="form-control bg-white"
                                value={originalData?.folio || 'N/A'}
                                disabled
                              />
                            </div>
                            <div className="col-md-4">
                              <label className="form-label text-muted">Fecha de Creación</label>
                              <input
                                type="text"
                                className="form-control bg-white"
                                value={originalData?.createdAt || 'N/A'}
                                disabled
                              />
                            </div>
                            <div className="col-md-4">
                              <label className="form-label text-muted">Fecha de Activación</label>
                              <input
                                type="text"
                                className="form-control bg-white"
                                value={originalData?.fechaActivacion || 'N/A'}
                                disabled
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="alert alert-info mt-3">
                        <small>
                          <i className="bi bi-info-circle me-1"></i>
                          Para modificar estos campos se requiere un proceso especial de soporte.
                        </small>
                      </div>
                    </div>
                  )}

                  {/* Tab: Acciones Especiales */}
                  {activeTab === 'acciones' && (
                    <div className="tab-pane fade show active">
                      <div className="alert alert-warning mb-4">
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        <strong>Zona de acciones especiales.</strong> Estas acciones pueden tener efectos irreversibles.
                      </div>

                      {/* Ocultar del usuario */}
                      <div className="card mb-3">
                        <div className="card-body d-flex justify-content-between align-items-center">
                          <div>
                            <h6 className="mb-1">
                              <i className="bi bi-eye-slash me-2"></i>
                              Ocultar del usuario
                            </h6>
                            <small className="text-muted">
                              El usuario no verá este registro en su panel, pero el admin sí puede verlo.
                            </small>
                          </div>
                          <button
                            className={`btn ${formData.oculto ? 'btn-success' : 'btn-secondary'}`}
                            onClick={handleToggleOculto}
                            disabled={saving}
                          >
                            {formData.oculto ? (
                              <>
                                <i className="bi bi-eye me-1"></i>
                                Mostrar
                              </>
                            ) : (
                              <>
                                <i className="bi bi-eye-slash me-1"></i>
                                Ocultar
                              </>
                            )}
                          </button>
                        </div>
                      </div>

                      {/* Modo Demo */}
                      <div className="card mb-3 border-info">
                        <div className="card-body d-flex justify-content-between align-items-center">
                          <div>
                            <h6 className="mb-1">
                              <i className="bi bi-flask me-2 text-info"></i>
                              Modo Demo
                            </h6>
                            <small className="text-muted">
                              Los tags en modo demo pueden ser reseteados por el usuario para demostraciones de ventas.
                            </small>
                          </div>
                          <button
                            className={`btn ${formData.esDemo ? 'btn-info' : 'btn-outline-info'}`}
                            onClick={handleToggleDemo}
                            disabled={saving}
                          >
                            {formData.esDemo ? (
                              <>
                                <i className="bi bi-flask-fill me-1"></i>
                                Desactivar Demo
                              </>
                            ) : (
                              <>
                                <i className="bi bi-flask me-1"></i>
                                Activar Demo
                              </>
                            )}
                          </button>
                        </div>
                      </div>

                      {/* Resetear código de activación */}
                      <div className="card mb-3 border-warning">
                        <div className="card-body">
                          <h6 className="mb-1">
                            <i className="bi bi-key me-2 text-warning"></i>
                            Resetear código de activación
                          </h6>
                          <small className="text-muted d-block mb-2">
                            Genera un nuevo código de 6 caracteres. El código anterior dejará de funcionar.
                          </small>

                          {newActivationCode && (
                            <div className="alert alert-success mb-2">
                              <strong>Nuevo código generado:</strong>
                              <span className="ms-2 font-monospace fs-4">{newActivationCode}</span>
                              <button
                                className="btn btn-sm btn-outline-success ms-2"
                                onClick={() => {
                                  navigator.clipboard.writeText(newActivationCode);
                                  toast.success('Código copiado al portapapeles');
                                }}
                              >
                                <i className="bi bi-clipboard"></i>
                              </button>
                            </div>
                          )}
                          <button
                            className="btn btn-warning"
                            onClick={handleResetCodigo}
                            disabled={saving}
                          >
                            <i className="bi bi-arrow-clockwise me-1"></i>
                            {newActivationCode ? 'Regenerar código' : 'Generar nuevo código'}
                          </button>
                        </div>
                      </div>

                      {/* Resetear datos */}
                      <div className="card mb-3" style={{ borderColor: '#f59e0b' }}>
                        <div className="card-body">
                          <h6 className="mb-1" style={{ color: '#92400e' }}>
                            <i className="bi bi-arrow-repeat me-2"></i>
                            Resetear datos de la mascota
                          </h6>
                          <small className="text-muted d-block mb-3">
                            Elimina datos específicos sin borrar la mascota.
                          </small>

                          <div className="row">
                            {Object.entries(resetLabels).map(([key, label]) => (
                              <div className="col-12 col-md-6 mb-2" key={key}>
                                <div
                                  className="form-check"
                                  style={{
                                    padding: '6px 8px 6px 32px',
                                    backgroundColor: resetFlags[key] ? '#fef3c7' : 'transparent',
                                    borderRadius: '6px',
                                    transition: 'background-color 0.15s',
                                  }}
                                >
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={`reset-${key}`}
                                    checked={resetFlags[key]}
                                    onChange={(e) => setResetFlags(prev => ({ ...prev, [key]: e.target.checked }))}
                                    disabled={resetting}
                                  />
                                  <label className="form-check-label" htmlFor={`reset-${key}`}
                                    style={{ fontSize: '14px', cursor: 'pointer' }}>
                                    {label}
                                  </label>
                                </div>
                              </div>
                            ))}
                          </div>

                          {!showResetConfirm ? (
                            <button
                              className="btn mt-2"
                              style={{ backgroundColor: '#f59e0b', color: 'white', borderColor: '#d97706' }}
                              onClick={() => {
                                if (selectedResetCount === 0) {
                                  toast.error('Selecciona al menos una categoría');
                                  return;
                                }
                                setShowResetConfirm(true);
                              }}
                              disabled={saving || resetting || selectedResetCount === 0}
                            >
                              <i className="bi bi-trash me-1"></i>
                              Eliminar seleccionados ({selectedResetCount})
                            </button>
                          ) : (
                            <div className="alert mt-2" style={{ backgroundColor: '#fef3c7', borderColor: '#fde68a' }}>
                              <p className="mb-2" style={{ color: '#92400e', fontWeight: 600 }}>
                                <i className="bi bi-exclamation-triangle me-1"></i>
                                ¿Estás seguro? Se eliminarán:
                              </p>
                              <ul className="mb-2" style={{ fontSize: '13px', color: '#78350f' }}>
                                {Object.entries(resetFlags).filter(([, v]) => v).map(([k]) => (
                                  <li key={k}>{resetLabels[k]}</li>
                                ))}
                              </ul>
                              <p className="mb-2" style={{ fontSize: '13px', color: '#dc2626', fontWeight: 600 }}>
                                Esta acción NO se puede deshacer.
                              </p>
                              <div className="d-flex gap-2">
                                <button
                                  className="btn btn-sm"
                                  style={{ backgroundColor: '#dc2626', color: 'white' }}
                                  onClick={handleResetDatos}
                                  disabled={resetting}
                                >
                                  {resetting ? (
                                    <><span className="spinner-border spinner-border-sm me-1"></span>Eliminando...</>
                                  ) : (
                                    <><i className="bi bi-trash me-1"></i>Confirmar eliminación</>
                                  )}
                                </button>
                                <button
                                  className="btn btn-sm btn-secondary"
                                  onClick={() => setShowResetConfirm(false)}
                                  disabled={resetting}
                                >
                                  Cancelar
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Eliminar registro */}
                      <div className="card border-danger">
                        <div className="card-body">
                          <h6 className="mb-1 text-danger">
                            <i className="bi bi-trash me-2"></i>
                            Eliminar registro permanentemente
                          </h6>
                          <small className="text-muted d-block mb-3">
                            Esta acción NO se puede deshacer. El registro será eliminado de la base de datos.
                          </small>

                          {!showDeleteConfirm ? (
                            <button
                              className="btn btn-outline-danger"
                              onClick={() => setShowDeleteConfirm(true)}
                              disabled={saving}
                            >
                              <i className="bi bi-trash me-1"></i>
                              Eliminar registro
                            </button>
                          ) : (
                            <div className="alert alert-danger">
                              <p className="mb-2">
                                <strong>Para confirmar, escribe:</strong> ELIMINAR_PERMANENTEMENTE
                              </p>
                              <div className="input-group mb-2">
                                <input
                                  type="text"
                                  className="form-control"
                                  value={deleteConfirmText}
                                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                                  placeholder="Escribe aquí..."
                                />
                              </div>
                              <div className="d-flex gap-2">
                                <button
                                  className="btn btn-danger"
                                  onClick={handleDelete}
                                  disabled={deleteConfirmText !== 'ELIMINAR_PERMANENTEMENTE' || saving}
                                >
                                  <i className="bi bi-trash me-1"></i>
                                  Confirmar eliminación
                                </button>
                                <button
                                  className="btn btn-secondary"
                                  onClick={() => {
                                    setShowDeleteConfirm(false);
                                    setDeleteConfirmText('');
                                  }}
                                >
                                  Cancelar
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Mensaje de error */}
                {error && (
                  <div className="alert alert-danger mt-3">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {error}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={saving}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving || loading || activeTab === 'acciones'}
            >
              {saving ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  Guardando...
                </>
              ) : (
                <>
                  <i className="bi bi-check-circle me-2"></i>
                  Guardar Cambios
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EditarRegistroModal;
